import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Edit, Check, Close, Delete, RotateLeft, Reply, Upload } from "@mui/icons-material";
import { Box, Typography, Avatar, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Tooltip } from "@mui/material";
import styled from "@emotion/styled";
import { $getRoot, $getSelection, $isRangeSelection } from "lexical";
import PrivacyChip from "./PrivacyChip";
import PrivacyDropdown from "./PrivacyDropdown";
import MarkdownEditor from "./MarkdownEditor";
import MarkdownDisplay from "./MarkdownDisplay";
import { useHasPermission } from "../utils/permissionUtils";
import { getAvatarUrl } from "../utils/avatarUtils";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addComment, deleteComment, editComment, restoreComment } from "../redux/ducks/comment";
import FilePondUploader from "./FilePondUploader";
import { restoreAttachments, transformAttachments } from "../utils/uploadUtils";
import AttachmentBox from "./AttachmentBox";
import { showAlert } from "../redux/slices/alerts";
import { extractPlainText, runContentFilter } from "../utils/contentFilter";
import { formatDateTime, formatCommentTime } from "../utils/dateUtils";

const PRIVACY_RANK = { Public: 0, Internal: 1, Private: 2 };

const CommentUserBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    padding: theme.spacing(1, 0, 1),
    gap: theme.spacing(1),
}));

const CommentContentBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0, 2, 0, 6),
}));

const CommentTime = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
}));

const ThreadBox = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 0),
    position: "relative",
}));

const NewCommentArea = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
}));

const UserNameBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const CommentNumber = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    fontFamily: "monospace",
    fontWeight: theme.typography.fontWeightMedium,
    flexShrink: 0,
    cursor: "help",
}));

const PushStateBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));

const SendToBoardButton = styled(IconButton)(({ theme }) => ({
    padding: theme.spacing(0.25),
    color: theme.palette.warning.main,
}));

const DialogNote = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
}));

const DeletedCommentContent = styled(Box)(({ theme }) => ({
    wordBreak: "break-all",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    textDecoration: "line-through",
}));

const highlightStyles = (theme, isHighlighted) => ({
    outline: isHighlighted ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
    outlineOffset: 2,
});

const CommentBox = styled(Box, { shouldForwardProp: (prop) => prop !== "isDeleted" && prop !== "privacy" && prop !== "isHighlighted" })(({ theme, isDeleted, privacy, isHighlighted }) => ({
    ...highlightStyles(theme, isHighlighted),
    backgroundColor: isDeleted
        ? theme.palette.action.disabled
        : privacy === "Internal"
        ? theme.palette.internal.light
        : privacy === "Private"
        ? theme.palette.private.light
        : theme.palette.background.paper,
    border:
        isDeleted || privacy !== "Public"
            ? `1px solid ${
                  isDeleted
                      ? theme.palette.inverted.main
                      : privacy === "Internal"
                      ? theme.palette.internal.main
                      : privacy === "Private"
                      ? theme.palette.private.main
                      : theme.palette.background.paper
              }`
            : "none",
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
}));

const ReplyBox = styled(Box, { shouldForwardProp: (prop) => prop !== "isDeleted" && prop !== "privacy" && prop !== "isHighlighted" })(({ theme, isDeleted, privacy, isHighlighted }) => ({
    ...highlightStyles(theme, isHighlighted),
    backgroundColor: isDeleted
        ? theme.palette.action.disabled
        : privacy === "Internal"
        ? theme.palette.internal.light
        : privacy === "Private"
        ? theme.palette.private.light
        : theme.palette.background.paper,
    border:
        isDeleted || privacy !== "Public"
            ? `1px solid ${
                  isDeleted
                      ? theme.palette.inverted.main
                      : privacy === "Internal"
                      ? theme.palette.internal.main
                      : privacy === "Private"
                      ? theme.palette.private.main
                      : theme.palette.background.paper
              }`
            : "none",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(0),
    padding: theme.spacing(1),
    borderLeft: `5px solid ${theme.palette.primary.main}`,
}));

const CommentActionsRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const CancelButton = styled(Button)(({ theme }) => ({
    marginRight: theme.spacing(1),
}));

const ClickableAvatar = styled(Avatar)(() => ({
    cursor: "pointer",
}));

const ClickableUserName = styled(Typography)(() => ({
    cursor: "pointer",
}));

const CommentToolbar = styled(Box)(() => ({
    marginLeft: "auto",
    display: "flex",
}));

const AddCommentButton = styled(Button)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const PRIVACY_LEVELS = ["Public", "Internal", "Private"];

const CommentThread = ({
    parentType,
    parentId,
    comments = [],
    onChanged,
    isLocked = false,
    readOnly = false,
    lockPrivacyTo = null,
    defaultPrivacy = "Public",
    composerDisabled = false,
    composerOpen,
    onComposerClose,
    emptyText = "No comments yet.",
    showPrivacyChip = true,
    onRetryPush = null,
    retryingPushId = null,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const userId = useSelector((state) => state.auth.userId);
    const contentFilter = useSelector((state) => state.config.contentFilter);

    const composerControlled = composerOpen !== undefined;
    const [internalComposerOpen, setInternalComposerOpen] = useState(false);
    const showComposer = composerControlled ? composerOpen : internalComposerOpen;

    const [localComments, setLocalComments] = useState([]);
    const [editingState, setEditingState] = useState({});
    const [editingCommentAttachments, setEditingCommentAttachments] = useState([]);
    const [replyTarget, setReplyTarget] = useState(null);
    const [replyContent, setReplyContent] = useState("");
    const [replyPrivacy, setReplyPrivacy] = useState(lockPrivacyTo || defaultPrivacy);
    const [replyAttachments, setReplyAttachments] = useState([]);
    const replyComposerRef = useRef(null);
    const replyEditorRef = useRef(null);
    const [newCommentContent, setNewCommentContent] = useState("");
    const [newCommentPrivacy, setNewCommentPrivacy] = useState(lockPrivacyTo || defaultPrivacy);
    const [newCommentAttachments, setNewCommentAttachments] = useState([]);
    const newCommentEditorRef = useRef(null);
    const [isNewCommentFocused, setIsNewCommentFocused] = useState(false);
    const canEditComment = hasPermission("editComment");
    const canDeleteComment = hasPermission("deleteComment");
    const canRestoreComment = hasPermission("restoreComment");
    const canSeeDeleted = hasPermission("canSeeDeleted");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [commentToRestore, setCommentToRestore] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [highlightedNumber, setHighlightedNumber] = useState(null);
    const highlightTimerRef = useRef(null);

    useEffect(() => {
        const source = Array.isArray(comments) ? comments : [];
        const sortedComments = [...source].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const threadedComments = sortedComments.reduce((acc, comment) => {
            if (comment._id.toString() === comment.thread.toString()) {
                if (comment.isDeleted && !canSeeDeleted) {
                    return acc;
                }
                let replies = sortedComments
                    .filter((reply) => reply.thread.toString() === comment._id.toString() && reply._id.toString() !== reply.thread.toString())
                    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                if (!canSeeDeleted) {
                    replies = replies.filter((reply) => !reply.isDeleted);
                }
                acc.push({ ...comment, replies });
            }
            return acc;
        }, []);
        setLocalComments(threadedComments);
    }, [comments, canSeeDeleted]);

    useEffect(() => {
        if (isNewCommentFocused && newCommentEditorRef.current) {
            const editorElement = newCommentEditorRef.current.querySelector('[contenteditable="true"]');
            if (editorElement) {
                setTimeout(() => {
                    editorElement.focus();
                }, 0);
            }
        }
    }, [isNewCommentFocused]);

    const emit = useCallback((nextComments) => {
        if (typeof onChanged === "function") onChanged(nextComments);
    }, [onChanged]);

    const closeComposer = useCallback(() => {
        if (composerControlled) {
            if (typeof onComposerClose === "function") onComposerClose();
        } else {
            setInternalComposerOpen(false);
        }
    }, [composerControlled, onComposerClose]);

    const handleDeleteClick = (comment) => {
        setCommentToDelete(comment);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (commentToDelete) {
            dispatch(
                deleteComment(parentType, parentId, commentToDelete._id, () => {
                    emit(comments.map((c) => (c._id === commentToDelete._id ? { ...c, isDeleted: true } : c)));
                    setDeleteDialogOpen(false);
                    setCommentToDelete(null);
                })
            );
        }
    };

    const handleRestoreClick = (comment) => {
        setCommentToRestore(comment);
        setRestoreDialogOpen(true);
    };

    const handleConfirmRestore = () => {
        if (commentToRestore) {
            dispatch(
                restoreComment(parentType, parentId, commentToRestore._id, () => {
                    emit(comments.map((c) => (c._id === commentToRestore._id ? { ...c, isDeleted: false } : c)));
                    setRestoreDialogOpen(false);
                    setCommentToRestore(null);
                })
            );
        }
    };

    const handleEditClick = (comment, floor = "Public") => {
        setEditingState((prev) => ({
            ...prev,
            [comment._id]: {
                // Seeded from the portable copy: what is shown has this origin and this card's id baked into its image srcs, and saving that would stop the picture resolving the
                // moment either changed.
                content: comment.sourceContent || comment.content,
                privacy: PRIVACY_LEVELS.indexOf(floor) > PRIVACY_LEVELS.indexOf(comment.privacy) ? floor : comment.privacy,
                attachments: transformAttachments(comment.attachments),
            },
        }));
    };

    // Threads stay one level deep: replying to a reply opens its root's composer and references the reply as #N.
    const handleReplyClick = (comment) => {
        const thread = String(comment.thread);
        const isReply = String(comment._id) !== thread;
        const rootPrivacy = comments.find((c) => String(c._id) === thread)?.privacy || "Public";
        const commentPrivacy = comment.privacy || "Public";
        const floor = PRIVACY_RANK[rootPrivacy] > PRIVACY_RANK[commentPrivacy] ? rootPrivacy : commentPrivacy;
        const clickedPrivacy = lockPrivacyTo || (PRIVACY_RANK[floor] > PRIVACY_RANK[defaultPrivacy] ? floor : defaultPrivacy);
        if (replyTarget?.thread !== thread) {
            replyEditorRef.current = null;
            setReplyContent("");
            setReplyAttachments([]);
        }
        setReplyPrivacy(clickedPrivacy);
        setReplyTarget({ thread, floor, floorId: String(comment._id), number: isReply ? comment.number : null, seq: (replyTarget?.seq || 0) + 1 });
    };

    const handleReplyCancel = () => {
        replyEditorRef.current = null;
        setReplyTarget(null);
        setReplyContent("");
        setReplyAttachments([]);
        setReplyPrivacy(lockPrivacyTo || defaultPrivacy);
    };

    useEffect(() => {
        if (!replyTarget) return;
        const editor = replyEditorRef.current;
        if (editor && replyTarget.number) {
            const reference = `#${replyTarget.number}`;
            editor.update(() => {
                const root = $getRoot();
                if (new RegExp(`${reference}\\b`).test(root.getTextContent())) return;
                root.selectEnd();
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;
                const before = selection.anchor.getNode().getTextContent().slice(0, selection.anchor.offset);
                selection.insertText(`${before && !/\s$/.test(before) ? " " : ""}${reference} `);
            });
        }
        replyComposerRef.current?.scrollIntoView({ block: "center" });
        if (editor) {
            editor.focus(undefined, { defaultSelection: "rootEnd" });
        }
    }, [replyTarget]);

    const handleSave = useCallback(() => {
        const editingCommentId = Object.keys(editingState)[0];
        if (editingCommentId) {
            const { content, privacy, attachments } = editingState[editingCommentId];
            const check = runContentFilter(extractPlainText(content), contentFilter);
            if (check.blocked) {
                dispatch(showAlert({ severity: "warning", message: check.reason }));
                return;
            }
            const effectivePrivacy = lockPrivacyTo || privacy;
            dispatch(
                editComment(parentType, parentId, editingCommentId, { content, privacy: effectivePrivacy, attachments: restoreAttachments(editingCommentAttachments, attachments) }, (response) => {
                    if (!response?.comment) return;
                    const updatedComment = response.comment;
                    const raise = (c) => c.thread === updatedComment._id && c._id !== updatedComment._id && PRIVACY_LEVELS.indexOf(c.privacy) < PRIVACY_LEVELS.indexOf(updatedComment.privacy);
                    emit(comments.map((c) => (c._id === updatedComment._id ? updatedComment : raise(c) ? { ...c, privacy: updatedComment.privacy } : c)));
                    setEditingState({});
                    setEditingCommentAttachments([]);
                })
            );
        }
    }, [dispatch, parentType, parentId, comments, editingState, editingCommentAttachments, contentFilter, lockPrivacyTo, emit]);

    const handleCancelEdit = () => {
        setEditingState({});
    };

    const handleEditorChange = useCallback((newContent) => {
        setNewCommentContent(newContent);
    }, []);

    const handleAddComment = useCallback(() => {
        if (newCommentContent.trim()) {
            const check = runContentFilter(extractPlainText(newCommentContent), contentFilter);
            if (check.blocked) {
                dispatch(showAlert({ severity: "warning", message: check.reason }));
                return;
            }
            const effectivePrivacy = lockPrivacyTo || newCommentPrivacy;
            dispatch(
                addComment(parentType, parentId, { content: newCommentContent, privacy: effectivePrivacy, attachments: restoreAttachments(newCommentAttachments) }, (response) => {
                    if (!response?.comment) return;
                    emit([response.comment, ...comments]);
                    setNewCommentContent("");
                    setNewCommentPrivacy(lockPrivacyTo || defaultPrivacy);
                    setNewCommentAttachments([]);
                    closeComposer();
                })
            );
        }
    }, [dispatch, parentType, parentId, newCommentContent, newCommentPrivacy, newCommentAttachments, comments, contentFilter, lockPrivacyTo, defaultPrivacy, emit, closeComposer]);

    const handleAddReply = useCallback((thread) => {
        if (replyContent.trim()) {
            const check = runContentFilter(extractPlainText(replyContent), contentFilter);
            if (check.blocked) {
                dispatch(showAlert({ severity: "warning", message: check.reason }));
                return;
            }
            const effectivePrivacy = lockPrivacyTo || replyPrivacy;
            dispatch(addComment(parentType, parentId, { content: replyContent, privacy: effectivePrivacy, attachments: restoreAttachments(replyAttachments), thread }, (response) => {
                if (!response?.comment) return;
                emit([response.comment, ...comments]);
                replyEditorRef.current = null;
                setReplyContent("");
                setReplyPrivacy(lockPrivacyTo || defaultPrivacy);
                setReplyAttachments([]);
                setReplyTarget(null);
            }));
        }
    }, [dispatch, parentType, parentId, replyContent, replyPrivacy, replyAttachments, comments, contentFilter, lockPrivacyTo, defaultPrivacy, emit]);

    const handleUserClick = (id) => {
        navigate(`/user/${id}`);
    };

    const handlePrivacyChange = (event) => {
        setNewCommentPrivacy(event.target.value);
    };

    const handleNewCommentFocus = useCallback(() => setIsNewCommentFocused(true), []);
    const handleNewCommentBlur = useCallback(() => setIsNewCommentFocused(false), []);

    const handleEditPrivacyChange = (event, commentId) => {
        setEditingState((prev) => ({
            ...prev,
            [commentId]: { ...prev[commentId], privacy: event.target.value },
        }));
    };

    const handleAttachmentReupload = useCallback((commentId, newAttachment) => {
        emit(comments.map((c) => (c._id === commentId ? { ...c, attachments: [...(c.attachments || []), newAttachment] } : c)));
    }, [comments, emit]);

    const renderComposer = (onSubmit, submitLabel, onCancel, placeholder, privacyFloor = "Public", isReply = false) => (
        <NewCommentArea ref={isReply ? replyComposerRef : undefined}>
            {isReply ? (
                <MarkdownEditor disabled={composerDisabled} placeholder={placeholder} onChange={setReplyContent} autoFocus={false} editorRef={replyEditorRef} />
            ) : (
                <Box ref={newCommentEditorRef} onFocus={handleNewCommentFocus} onBlur={handleNewCommentBlur}>
                    <MarkdownEditor disabled={composerDisabled} placeholder={placeholder} onChange={handleEditorChange} autoFocus={false} />
                </Box>
            )}
            <FilePondUploader setAttachments={isReply ? setReplyAttachments : setNewCommentAttachments} onUploadStatusChange={setIsUploading} />
            <CommentActionsRow>
                {lockPrivacyTo ? (
                    <Box />
                ) : (
                    <PrivacyDropdown value={isReply ? replyPrivacy : newCommentPrivacy} onChange={isReply ? (event) => setReplyPrivacy(event.target.value) : handlePrivacyChange} floor={privacyFloor} />
                )}
                <Box>
                    <CancelButton variant="outlined" size="small" onClick={onCancel}>
                        Cancel
                    </CancelButton>
                    <Button disabled={composerDisabled || isUploading} variant="contained" size="small" onClick={onSubmit}>
                        {submitLabel}
                    </Button>
                </Box>
            </CommentActionsRow>
        </NewCommentArea>
    );

    const commentRefNumbers = useMemo(() => {
        const numbers = new Set();
        localComments.forEach((comment) => {
            if (comment.number) numbers.add(comment.number);
            (comment.replies || []).forEach((reply) => {
                if (reply.number) numbers.add(reply.number);
            });
        });
        return numbers;
    }, [localComments]);

    const newestCommentId = useMemo(() => {
        let newest = null;
        let newestTime = 0;
        localComments.forEach((comment) => {
            if (!comment.isDeleted) {
                const t = new Date(comment.createdAt).getTime();
                if (t > newestTime) { newestTime = t; newest = comment._id; }
            }
            (comment.replies || []).forEach((reply) => {
                if (!reply.isDeleted) {
                    const t = new Date(reply.createdAt).getTime();
                    if (t > newestTime) { newestTime = t; newest = reply._id; }
                }
            });
        });
        return newest;
    }, [localComments]);

    useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

    const handleThreadClick = useCallback((event) => {
        const anchor = event.target.closest?.('a[href^="#comment-"]');
        if (!anchor) return;
        event.preventDefault();
        const number = anchor.getAttribute("href").slice("#comment-".length);
        const target = event.currentTarget.querySelector(`[data-comment-number="${number}"]`);
        if (!target) return;
        target.scrollIntoView({ block: "center" });
        setHighlightedNumber(Number(number));
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightedNumber(null), 2000);
    }, []);

    const renderNumber = (comment) =>
        comment.number ? (
            <Tooltip title={`Comment #${comment.number} — write #${comment.number} in another comment to link back here`}>
                <CommentNumber variant="subtitle2">#{comment.number}</CommentNumber>
            </Tooltip>
        ) : null;

    const renderPrivacy = (comment) => (showPrivacyChip && comment.privacy !== "Public" ? <PrivacyChip privacy={comment.privacy} /> : null);

    const renderPushState = (comment) => {
        if (comment.externalPushFailedAt) {
            const busy = retryingPushId && String(retryingPushId) === String(comment._id);
            return (
                <PushStateBox>
                    <Tooltip title="Pollux could not confirm this comment reached the connected BugHerd board, so the client may not be able to see it.">
                        <Chip variant="outlined" size="small" color="warning" label="Not on board" />
                    </Tooltip>
                    {onRetryPush && !comment.isDeleted && !readOnly && !isLocked && (
                        <Tooltip title="Send this comment to the board again">
                            <span>
                                <SendToBoardButton size="small" aria-label="Send this comment to the board again" disabled={!!busy} onClick={() => onRetryPush(comment._id)}>
                                    {busy ? <CircularProgress size={14} /> : <Upload fontSize="small" />}
                                </SendToBoardButton>
                            </span>
                        </Tooltip>
                    )}
                </PushStateBox>
            );
        }
        if (comment.isDeleted && comment.externalDeletedAt) {
            return (
                <Tooltip title="This comment was deleted on the connected BugHerd board, so Pollux hid its copy to match.">
                    <Chip variant="outlined" size="small" color="info" label="Removed on board" />
                </Tooltip>
            );
        }
        if (comment.isDeleted && comment.externalId) {
            return (
                <Tooltip title="BugHerd cannot remove a posted comment, so this one is deleted in Pollux only and the client still sees it on their board.">
                    <Chip variant="outlined" size="small" color="warning" label="Still on board" />
                </Tooltip>
            );
        }
        if (comment.externalDivergedAt) {
            return (
                <Tooltip title="Edited after it reached the connected BugHerd board. BugHerd cannot change a posted comment, so the client still sees the earlier text.">
                    <Chip variant="outlined" size="small" color="warning" label="Edit not on board" />
                </Tooltip>
            );
        }
        if (comment.externalImagePushFailedAt) {
            return (
                <Tooltip title="An image in this comment could not be uploaded to the connected BugHerd board, so the client sees a note where the picture should be.">
                    <Chip variant="outlined" size="small" color="warning" label="Image not on board" />
                </Tooltip>
            );
        }
        if (comment.externalPushTruncatedAt) {
            return (
                <Tooltip title="BugHerd limits how long a comment can be, so the copy on the board is shortened and points back here for the rest.">
                    <Chip variant="outlined" size="small" color="info" label="Shortened on board" />
                </Tooltip>
            );
        }
        return null;
    };

    const renderToolbar = (comment, floor = "Public", root = comment) => {
        if (readOnly) return null;
        return (
            <CommentToolbar>
                {comment.isDeleted ? (
                    canRestoreComment && !isLocked && (
                        <IconButton onClick={() => handleRestoreClick(comment)}>
                            <RotateLeft />
                        </IconButton>
                    )
                ) : editingState[comment._id] ? (
                    isUploading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <>
                            <IconButton onClick={handleSave}>
                                <Check />
                            </IconButton>
                            <IconButton onClick={handleCancelEdit}>
                                <Close />
                            </IconButton>
                        </>
                    )
                ) : (
                    <>
                        {!root.isDeleted && !isLocked && (
                            <IconButton onClick={() => handleReplyClick(comment)}>
                                <Reply />
                            </IconButton>
                        )}
                        {(canEditComment || comment.user?._id === userId) && !isLocked && (
                            <Tooltip title={comment.externalId ? "Edit — BugHerd cannot change a posted comment, so the client keeps seeing the earlier text" : "Edit"}>
                                <IconButton onClick={() => handleEditClick(comment, floor)}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        )}
                        {(canDeleteComment || comment.user?._id === userId) && !isLocked && (
                            <Tooltip title={comment.externalId ? "Delete — BugHerd cannot remove a posted comment, so the client keeps seeing it" : "Delete"}>
                                <IconButton onClick={() => handleDeleteClick(comment)}>
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                )}
            </CommentToolbar>
        );
    };

    const renderBody = (comment, floor = "Public") => (
        <CommentContentBox>
            {comment.isDeleted ? (
                <DeletedCommentContent>
                    <MarkdownDisplay content={comment.content} />
                </DeletedCommentContent>
            ) : editingState[comment._id] ? (
                <>
                    <MarkdownEditor
                        key={comment._id}
                        initialContent={editingState[comment._id].content}
                        onChange={(newContent) =>
                            setEditingState((prev) => ({
                                ...prev,
                                [comment._id]: { ...prev[comment._id], content: newContent },
                            }))
                        }
                        autoFocus={true}
                    />
                    <FilePondUploader setAttachments={setEditingCommentAttachments} files={editingState[comment._id].attachments} onUploadStatusChange={setIsUploading} />
                    {!lockPrivacyTo && <PrivacyDropdown value={editingState[comment._id].privacy} onChange={(event) => handleEditPrivacyChange(event, comment._id)} floor={floor} />}
                </>
            ) : (
                <MarkdownDisplay content={comment.content} commentRefNumbers={commentRefNumbers} />
            )}
            {comment.attachments !== undefined &&
                comment.attachments.length > 0 &&
                comment.attachments.map((attachment) => (
                    <AttachmentBox key={attachment._id} attachment={attachment} onReupload={(newAttachment) => handleAttachmentReupload(comment._id, newAttachment)} />
                ))}
        </CommentContentBox>
    );

    return (
        <>
            {!readOnly && showComposer && renderComposer(handleAddComment, "Comment", closeComposer, "Add a comment...")}

            {!readOnly && !composerControlled && !showComposer && (
                <AddCommentButton variant="outlined" size="small" onClick={() => setInternalComposerOpen(true)}>
                    Add a comment
                </AddCommentButton>
            )}

            <ThreadBox onClick={handleThreadClick}>
                {localComments.length === 0 ? (
                    <Typography>{emptyText}</Typography>
                ) : (
                    localComments.map((comment) => (
                        <CommentBox
                            key={comment._id}
                            isDeleted={comment.isDeleted}
                            privacy={comment.privacy}
                            isHighlighted={highlightedNumber === comment.number}
                            data-comment-number={comment.number}
                        >
                            <CommentUserBox>
                                <ClickableAvatar src={getAvatarUrl(comment.user)} alt={comment.user?.fullName} onClick={() => comment.user?._id && handleUserClick(comment.user._id)} />
                                <Box>
                                    <UserNameBox>
                                        {renderNumber(comment)}
                                        <ClickableUserName variant="subtitle2" onClick={() => comment.user?._id && handleUserClick(comment.user._id)}>
                                            {comment.user?.fullName}
                                        </ClickableUserName>
                                        {comment.isDeleted && <Chip variant="outlined" size="small" color="error" label="Deleted" />}
                                        {renderPrivacy(comment)}
                                        {renderPushState(comment)}
                                        {String(comment._id) === String(newestCommentId) && localComments.length > 1 && <Chip size="small" color="primary" label="Latest" />}
                                    </UserNameBox>
                                    <Tooltip title={formatDateTime(comment.createdAt)} placement="right"><CommentTime variant="caption">{formatCommentTime(comment.createdAt)}</CommentTime></Tooltip>
                                </Box>
                                {renderToolbar(comment)}
                            </CommentUserBox>
                            {renderBody(comment)}
                            <CommentContentBox>
                                {comment.replies &&
                                    comment.replies.length > 0 &&
                                    comment.replies.map((reply) => (
                                        <ReplyBox
                                            key={reply._id}
                                            isDeleted={reply.isDeleted}
                                            privacy={reply.privacy}
                                            isHighlighted={highlightedNumber === reply.number}
                                            data-comment-number={reply.number}
                                        >
                                            <CommentUserBox>
                                                <ClickableAvatar src={getAvatarUrl(reply.user)} alt={reply.user?.fullName} onClick={() => reply.user?._id && handleUserClick(reply.user._id)} />
                                                <Box>
                                                    <UserNameBox>
                                                        {renderNumber(reply)}
                                                        <ClickableUserName variant="subtitle2" onClick={() => reply.user?._id && handleUserClick(reply.user._id)}>
                                                            {reply.user?.fullName}
                                                        </ClickableUserName>
                                                        {reply.isDeleted && <Chip variant="outlined" size="small" color="error" label="Deleted" />}
                                                        {renderPrivacy(reply)}
                                                        {renderPushState(reply)}
                                                        {String(reply._id) === String(newestCommentId) && <Chip size="small" color="primary" label="Latest" />}
                                                    </UserNameBox>
                                                    <Tooltip title={formatDateTime(reply.createdAt)} placement="right"><CommentTime variant="caption">{formatCommentTime(reply.createdAt)}</CommentTime></Tooltip>
                                                </Box>
                                                {renderToolbar(reply, comment.privacy || "Public", comment)}
                                            </CommentUserBox>
                                            {renderBody(reply, comment.privacy || "Public")}
                                        </ReplyBox>
                                    ))}
                                {!readOnly && replyTarget?.thread === String(comment._id) &&
                                    renderComposer(() => handleAddReply(replyTarget.floorId), "Reply", handleReplyCancel, "Leave a reply...", replyTarget.floor, true)}
                            </CommentContentBox>
                        </CommentBox>
                    ))
                )}
            </ThreadBox>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Comment</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this comment?</Typography>
                    {commentToDelete?.externalId && <DialogNote variant="body2">It stays on the client's BugHerd board — BugHerd cannot remove a posted comment.</DialogNote>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
                <DialogTitle>Restore Comment</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to restore this comment?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmRestore} variant="contained" color="primary">
                        Restore
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CommentThread;
